import { Injectable } from '@angular/core';
import * as Constants from '../models/Constants';
import { HttpClient } from '@angular/common/http';
import { Global } from '../global/globalApi';
import { RootScopeService } from './root-scope.service';

@Injectable({
  providedIn: 'root'
})
export class PrivacyPolicyService {

  private readonly defaultCountryCode = 'US';

  constructor(public rootScope: RootScopeService, private http: HttpClient) { }

  /**
    * @author Amarjeet
    *getPrivacyPolicyStatus()-Method to get the privacy policy status
    */
  getPrivacyPolicyStatus() {
    const url = Global.domainUrl + Global.authenticationApis.getPrivacyPolicyStatus;
    return this.http.get(url, {
      headers: this.rootScope.getCustAccHeaders(Constants.apiVersions, this.rootScope.activeCustTab.customerSessionId),
      params: { clientId: this.rootScope.activeCustTab.clientId }
    });
  }

  /**CCR3-14638
    * @author Amarjeet
    *updatePrivacyPolicyStatus()-Method to update the privacy policy status
    */
  updatePrivacyPolicyStatus(clientId: string) {
    const url = Global.domainUrl + Global.authenticationApis.updatePrivacyPolicyStatus;
    return this.http.post(url, { clientId }, {
      headers: this.rootScope.getCustAccHeaders(Constants.apiVersions, this.rootScope.activeCustTab.customerSessionId)
    });
  }

  onDeliveryMethodChange(value: any, onRefresh: () => void): void {
    const d = this.rootScope.activeCustTab.privacyPolicyData;

    if (value?.phoneNumber !== undefined) {
      // A phone option was selected
      d.email       = '';
      d.customEmail = '';

      if (!value.type) {
        // UDP phone
        d.mobileNumber           = value.phoneNumber;
        d.countryCode            = value.countryCode || this.defaultCountryCode;
        d.isMobileSelected       = true;
        d.isCustomMobileSelected = false;
      } else {
        // sentinel phoneNumber is '' — only overwrite if a real number was passed
        if (value.phoneNumber) {
          d.customMobileNumber = value.phoneNumber;
        }
        d.mobileNumber           = '';
        d.countryCode            = this.defaultCountryCode;
        d.isMobileSelected       = false;
        d.isCustomMobileSelected = true;
      }
      d.isEmailSelected       = false;
      d.isCustomEmailSelected = false;

    } else if (value?.emailAdress !== undefined || value?.name === 'email') {
      // An email option was selected
      d.mobileNumber = '';

      if (!value.type) {
        // UDP email
        d.email                 = value.emailAdress;
        d.customEmail           = '';
        d.isEmailSelected       = true;
        d.isCustomEmailSelected = false;
      } else {
        // Custom email sentinel
        d.isEmailSelected       = false;
        d.isCustomEmailSelected = true;
        d.email                 = '';
      }
      d.isMobileSelected       = false;
      d.isCustomMobileSelected = false;
      // preserve customMobileNumber so switching back restores valid state

    } else if (!value) {
      // genuine reset only — ignore unrecognised values (e.g. language strings leaking from sibling radio group)
      d.mobileNumber           = '';
      d.email                  = '';
      d.countryCode            = this.defaultCountryCode;
      d.isEmailSelected        = false;
      d.isMobileSelected       = false;
      d.isCustomMobileSelected = false;
    }

    onRefresh();
  }

  isDeliverySelected(): boolean {
    const d = this.rootScope.activeCustTab.privacyPolicyData;
    return d.isMobileSelected || d.isEmailSelected || d.isCustomMobileSelected || d.isCustomEmailSelected;
  }

  isDeliveryValid(): boolean {
    const d = this.rootScope.activeCustTab.privacyPolicyData;
    if (d.isMobileSelected || d.isEmailSelected) return true;
    if (d.isCustomMobileSelected) return (d.customMobileNumber?.length ?? 0) > 0;
    if (d.isCustomEmailSelected)  return Constants.PATTERNS.EMAIL.test(d.customEmail || '');
    return false;
  }

  onCustomMobileFocus(customMobileOption: any, onRefresh: () => void): void {
    const d = this.rootScope.activeCustTab.privacyPolicyData;
    if (d.isEmailSelected || d.isCustomEmailSelected) return;
    d.isCustomMobileSelected = true;
    d.isEmailSelected        = false;
    d.isMobileSelected       = false;
    d.isCustomEmailSelected  = false;
    d.defaultSelectedValue   = customMobileOption;
    d.mobileNumber           = '';
    d.email                  = '';
    onRefresh();
  }

  onCustomEmailFocus(customEmailOption: any, onRefresh: () => void): void {
    const d = this.rootScope.activeCustTab.privacyPolicyData;
    customEmailOption.emailAdress = d.customEmail || '';
    d.defaultSelectedValue   = customEmailOption;
    d.isCustomEmailSelected  = true;
    d.isMobileSelected       = false;
    d.isEmailSelected        = false;
    d.isCustomMobileSelected = false;
    d.mobileNumber           = '';
    onRefresh();
  }

  onTelInputChange(event: any, onRefresh: () => void): void {
    const d = this.rootScope.activeCustTab.privacyPolicyData;
    const newCountry     = event.detail.country || this.defaultCountryCode;
    const nationalNumber = event.detail.nationalNumber || '';
    const callingCode    = event.detail.countryCallingCode || '';

    // Country changed: clear stored number immediately so Send disables before the user retypes.
    if (newCountry !== d.countryCode) {
      d.mobileNumber       = '';
      d.customMobileNumber = '';
      d.countryCode        = newCountry;
      event.srcElement.errorMessage = '';
      onRefresh();
      return;
    }

    d.mobileNumber = nationalNumber;
    d.countryCode  = newCountry;

    // Reject stale events carrying a number from the previous country's calling code.
    if (callingCode && nationalNumber.startsWith('+') && !nationalNumber.startsWith(callingCode)) {
      d.customMobileNumber = '';
      event.srcElement.errorMessage = '';
      onRefresh();
      return;
    }

    // A calling-code-only event (e.g. '+1') fires after a country change.
    // Require subscriber digits beyond the calling code before accepting.
    const callingCodeLen = callingCode.replace(/\D/g, '').length;
    const allDigitLen    = nationalNumber.replace(/\D/g, '').length;
    const hasNumber      = nationalNumber.startsWith('+')
      ? (callingCodeLen > 0 ? allDigitLen > callingCodeLen : allDigitLen > 3)
      : allDigitLen > 0;

    if (hasNumber && !event.detail.isValid) {
      event.srcElement.errorMessage = 'Invalid Mobile number.';
      d.customMobileNumber = '';
    } else {
      event.srcElement.errorMessage = '';
      d.customMobileNumber = (event.detail.isValid && hasNumber) ? nationalNumber : '';
    }
    onRefresh();
  }

  /** Calls loadOTCConfig (authType: IDENTITY_VERIFICATION) to get the customer's registered phones and emails */
  loadDeliveryOptions(): void {
    const privacyData = this.rootScope.activeCustTab.privacyPolicyData;
    if (privacyData.mobileNumbers.length > 0 || privacyData.emails.length > 0) {
      return;
    }
    const url = Global.domainUrl + Global.authenticationApis.loadOTCConfig;
    privacyData.isLoadingDeliveryOptions = true;
    this.http.get(url, {
      headers: this.rootScope.getCustAccHeaders(Constants.apiVersions, this.rootScope.activeCustTab.customerSessionId),
      params: { clientId: this.rootScope.activeCustTab.clientId, authType: 'IDENTITY_VERIFICATION' }
    }).subscribe({
      next: (res: any) => {
        const deliveryMethods: any[] = res?.data?.deliveryMethods ?? [];
        privacyData.mobileNumbers = deliveryMethods
          .filter((m: any) => m.type === 'text')
          .flatMap((m: any) => m.details)
          .filter(Boolean);
        privacyData.emails = deliveryMethods
          .filter((m: any) => m.type === 'email')
          .flatMap((m: any) => m.details)
          .filter(Boolean);
        privacyData.isLoadingDeliveryOptions = false;
      },
      error: () => { privacyData.isLoadingDeliveryOptions = false; }
    });
  }

  /** Reads language options from the PRIVACY-POLICY-MULTI-LANG client property (same pattern as VAL) */
  loadLanguages(): { label: string; value: string }[] {
    const clientProps = this.rootScope.clientPropDataMap?.get(this.rootScope.activeCustTab.clientId);
    const langProp = clientProps?.find((p: any) => p?.propertyName === Constants.PRIVACY_POLICY_LANGUAGE_PROPERTYNAME);
    if (!langProp?.propertyValue) {
      return [{ label: 'English', value: 'en-US' }];
    }
    try {
      const parsed = JSON.parse(langProp.propertyValue);
      const langs = Object.keys(parsed).map(key => ({ label: key, value: parsed[key] }))
                          .sort((a, b) => a.label.localeCompare(b.label));
      return langs.length > 0 ? langs : [{ label: 'English', value: 'en-US' }];
    } catch {
      return [{ label: 'English', value: 'en-US' }];
    }
  }

  /** Orchestrates the full send flow: sets state flags, POSTs, updates status, resets data */
  sendPcsNotification(language: string, onReset: () => void): void {
    const d = this.rootScope.activeCustTab.privacyPolicyData;

    const payload = this.buildPayload(language);
    d.isSending = true;
    d.sendFailure = false;
    d.sendSuccess = false;

    this.postPcsNotification(payload).subscribe({
      next: (res: any) => {
        d.isSending = false;
        if (!res?.data?.trackingId) {
          d.sendFailure = true;
          return;
        }
        d.sendSuccess = true;
        this.resetPolicyData();
        onReset();
      },
      error: () => {
        d.isSending = false;
        d.sendFailure = true;
      }
    });

  }

  private buildPayload(language: string) {
    const d        = this.rootScope.activeCustTab.privacyPolicyData;
    const clientId = this.rootScope.activeCustTab.clientId;

    if (d.isMobileSelected) {
      return { clientId, mode: 'text', phoneNumber: d.mobileNumber, mblNmbrFrmtCd: d.countryCode, language };
    }
    if (d.isCustomMobileSelected) {
      return { clientId, mode: 'text', phoneNumber: d.customMobileNumber, mblNmbrFrmtCd: d.countryCode, language };
    }
    if (d.isEmailSelected) {
      return { clientId, mode: 'email', email: d.email, language };
    }
    // isCustomEmailSelected
    return { clientId, mode: 'email', email: d.customEmail, language };
  }

  private resetPolicyData(): void {
    const d = this.rootScope.activeCustTab.privacyPolicyData;
    d.defaultSelectedValue   = null;
    d.isMobileSelected       = false;
    d.isEmailSelected        = false;
    d.isCustomMobileSelected = false;
    d.isCustomEmailSelected  = false;
    d.mobileNumber           = '';
    d.email                  = '';
    d.customEmail            = '';
    d.customMobileNumber     = '';
    d.countryCode            = 'US';
  }

  private postPcsNotification(payload: {
    clientId: string; mode: string; phoneNumber?: string; mblNmbrFrmtCd?: string; language: string; email?: string;
  }) {
    const url = Global.domainUrl + Global.authenticationApis.sendPcsNotification;
    return this.http.post(url, payload, {
      headers: this.rootScope.getCustAccHeaders(Constants.apiVersions, this.rootScope.activeCustTab.customerSessionId)
    });
  }

}
